import type {
  InlineResponse2004,
  MultipleChoice,
  MultipleChoiceGroup,
} from '@ulysses-inc/harami_api_client'
import {
  addResponseMultipleChoices,
  createTemplateV3,
  getTemplate,
  getTemplatePagesWithRule,
  getTemplateResponseMultipleChoices,
} from './api/templateApi'
import { buildMigratedTemplateBody } from './domain/buildBody'
import { convertGridToTable } from './domain/converter'
import { diagnoseEligibility } from './domain/eligibility'
import {
  collectReferencedTemplateSpecificGroups,
  createChoiceIdMapping,
  rewriteTemplateSpecificChoiceReferences,
} from './domain/templateSpecificChoiceReferences'
import {
  MigrationDomainError,
  type ChoiceIdMapping,
  type MigrationSource,
  type MigrationWorkflowDependencies,
} from './domain/types'

const withoutId = ({ id: _id, ...choice }: MultipleChoice): MultipleChoice =>
  choice

export const fetchMigrationSource = async (
  templateId: number,
): Promise<MigrationSource> => {
  const [template, pagesWithRule, templateSpecificChoices] = await Promise.all([
    getTemplate(templateId),
    getTemplatePagesWithRule(templateId),
    getTemplateResponseMultipleChoices(templateId),
  ])

  return { template, pagesWithRule, templateSpecificChoices }
}

export const createTemplateSpecificChoices = async (
  sourceGroups: MultipleChoiceGroup[],
): Promise<ChoiceIdMapping> => {
  const targetGroups = await Promise.all(
    sourceGroups.map(group =>
      addResponseMultipleChoices({
        multipleChoice: group.responses.map(withoutId),
        isTemplateSpecific: true,
      }),
    ),
  )
  return createChoiceIdMapping(sourceGroups, targetGroups)
}

const defaultDependencies: MigrationWorkflowDependencies = {
  fetchSource: fetchMigrationSource,
  createChoices: createTemplateSpecificChoices,
  createTemplate: createTemplateV3,
}

export const runGridToTableMigration = async (
  templateId: number,
  dependencies: MigrationWorkflowDependencies = defaultDependencies,
): Promise<InlineResponse2004> => {
  const source = await dependencies.fetchSource(templateId)
  const eligibility = diagnoseEligibility(source)
  if (!eligibility.eligible) {
    throw new MigrationDomainError(eligibility.reasons)
  }

  const converted = convertGridToTable(source)
  const referencedGroups = collectReferencedTemplateSpecificGroups(
    converted.pages,
    source.templateSpecificChoices,
  )
  // 新しいひな形では、先に作成したひな形限定選択肢のIDを使う。
  // ひな形の作成に失敗した場合は、ひな形限定選択肢の作成から一連の処理をやり直す。
  const choiceMapping = await dependencies.createChoices(referencedGroups)
  const pages = rewriteTemplateSpecificChoiceReferences(
    converted.pages,
    choiceMapping,
  )
  const body = buildMigratedTemplateBody({
    sourceTemplateId: templateId,
    template: source.template,
    pages,
  })
  return dependencies.createTemplate(body)
}
